import {AiToolImpl} from "@me8eon/aiclient";
import {mustBeObjectWithFields, mustBeString} from "@me8eon/validate";
import {ElasticSearchConfig, ElasticSearchHit, ElasticSearchKnnSearchResponse} from "@me8eon/ai.tools.utils";
import { JwtTokenFn, tokenToUserData } from "@me8eon/authentication";
import { getWorkingStorage } from "@me8eon/react_storage";

/**
 * Converts an Elasticsearch hit to a formatted string.
 * @param hit
 * @param index
 */
const hitToString = (hit: ElasticSearchHit, index: number): string => {
    return `**Document ${index + 1}**:
     ${hit._source.title && `- Title: ${hit._source.title}\n`}
     - Score: ${hit._score}, 
     - Index: ${hit._index}, 
     - ID: ${hit._id}
     - Source: """${JSON.stringify(hit._source)}"""`;
};

type ToolArgs = {
    query: string
}

type ToolImpl = AiToolImpl<ToolArgs>

type Tool = (config: ElasticSearchConfig, jwtTokenFn: JwtTokenFn, scopeFn: () => "local" | "compass-wide") => ToolImpl

export const CompassSearchToolImpl: Tool = (esConfig, jwtTokenFn, scopeFn) => {
    return {
        type: "compassSearch",
        toolDefn: {
            type: 'function',
            name: "compassSearch",
            description: "This tool performs a deep search on the Compass Elasticsearch index using semantic_text fields - one field with vector (e5-small) and another with sparse (elser). It retrieves the most relevant news articles, announcements, blog posts, and community discussions from the internal Compass platform based on the provided query.",
            parameters: {
                type: "object",
                required: ["query"],
                additionalProperties: false,
                properties: {
                    query: {
                        type: "string",
                        description: "The search query string used to find relevant Compass content. Should be a concise and clear representation of the news, announcement, or community topic being sought. This query HAS TO BE IN ENGLISH as the underlying models are trained primarily on English data.",
                    },
                },
            },
        },
        validateArgs: mustBeObjectWithFields({query: mustBeString}),
        execute: async (indices, input) => {
            try {
                const {
                    elasticSearchUrl,
                    axios,
                    tokenFn,
                    blacklistedFields = [],
                } = esConfig;
                const { query } = input;
                const scope = scopeFn();
                if (indices.length === 0) throw new Error("No indicies provided to compass search tool");
                const top = 5;
                const indiciesAsString = indices.join(",");
                const url = `${elasticSearchUrl}/${indiciesAsString}/_search`;
                const headers = {
                    "Content-Type": "application/json",
                    Authorization: `ApiKey ${await tokenFn()}`,
                };

                const token = await jwtTokenFn();
                const { companyId } = await tokenToUserData(token, getWorkingStorage());

                const scopeFilter = scope === "local"
                    ? {
                        bool: {
                            should: [
                                {term: {"companyIdTags.keyword": companyId}},
                                {term: {isPublic: true}}
                            ],
                            minimum_should_match: 1
                        }
                    }
                    : undefined;

                const body = {
                    "_source": {
                        "excludes": [...blacklistedFields,
                            "denied_countries",
                            "denied_companies",
                            "denied_users",
                            "allowed_countries",
                            "allowed_companies",
                            "allowed_users",
                            "allowed_all_countries",
                            "allowed_all_companies",
                            "allowed_all_users",
                            "semantic_test",
                            "dense_vector",
                            "sparse_vector",
                            "companyTags",
                            "companyIdTags",
                            "categoryTags",
                            "allowed_empTypes"],
                    },
                    "size": top,
                    "retriever": {
                        "text_similarity_reranker": {
                            "retriever": {
                                "rrf": {
                                    "rank_window_size": 100,
                                    "rank_constant": 60,
                                    "retrievers": [
                                        {
                                            "standard": {
                                                "query": {
                                                    "match": {
                                                        "sparse_vector": query,
                                                    }
                                                }
                                            }
                                        },
                                        {
                                            "standard": {
                                                "query": {
                                                    "match": {
                                                        "dense_vector": query,
                                                    }
                                                }
                                            }
                                        }
                                    ]
                                }
                            },
                            "field": "semantic_text",
                            "inference_id": "cross-encoder-rerank",
                            "inference_text": query,
                            "rank_window_size": 5,
                            ...(scopeFilter ? {"filter": scopeFilter} : {})
                        },
                    },
                    "highlight": {
                        "fields": {
                            "sparse_vector": {
                                "type": "semantic",
                                "number_of_fragments": 3,
                                "order": "score"
                            },
                            "dense_vector": {
                                "type": "semantic",
                                "number_of_fragments": 3,
                                "order": "score"
                            }
                        }
                    }
                };


                const response = await axios.post<ElasticSearchKnnSearchResponse>(url, body, {headers: headers});
                const topDocuments = response.data?.hits?.hits?.slice(0, top).map((hit, index) => hitToString(hit, index));
                const content = `Here are the top ${topDocuments.length} Compass posts found:\n\n${topDocuments.join("\n\n")}`;
                return {
                    value: content,
                }

            } catch (e: any) {
                return {
                    value: "An error occurred while performing the compass search.",
                    _meta: {
                        errorMessage: String(e?.message ?? e),
                    }
                };
            }

        },
    };
};
